import * as React from "react";
import { styled, useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import CssBaseline from "@mui/material/CssBaseline";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { IconButton, Stack, useMediaQuery } from "@mui/material";
import { useLocation, useNavigate } from "react-router";
import { useState } from "react";
import { darkColors } from "../../theme/colors";
import { fontSizes } from "../../theme/font-sizes";
import XIcon from "@mui/icons-material/X"; // Replace with the correct icon import if needed

import { assetIcons, assetImages } from "../../constants/assets";
import { socialRedirectUrls } from "../../constants/urls";
import { Instagram, Telegram } from "@mui/icons-material";
import englishTranslations from "../../i18n/translations/en";
import ConfirmationModal from "../utils/popups/confirmation-popup";
import MobileBar from "./mobileBar";
import routes from "../../constants/routes";
import InlineSvg from "../utils/inline-svg";
import FacebookIcon from "@mui/icons-material/Facebook";
import TermsPrivacyLinks from "../utils/terms-privacy-links";

const drawerWidth = 270;
const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(8)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});
const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  position: "fixed", // Make the drawer fixed
  zIndex: 1200, // Ensure it's above other content
  top: 0,
  left: 0,
  height: "100vh", // Full height to cover the page
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

export default function MiniDrawer() {
  const theme = useTheme();
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("lg"));
  const navigate = useNavigate();
  const [openDialog, setOpenDialog] = useState(false);
  const [open, setOpen] = useState(true);
  const location = useLocation();
  const [openModal, setOpenModal] = useState(false);
  const [routePath, setRoutePath] = useState("");
  const handleOpen = (path) => {
    setRoutePath(path);
    setOpenModal(true);
  };

  // To close the modal
  const handleClose = () => {
    setOpenModal(false);
  };
  const navigateToMainMenu = () => {
    handleClose();
    navigate(routePath);
  };

  React.useEffect(() => {
    setOpen(!isMediumScreen);
  }, [isMediumScreen]);
  const isActive = (path) => {
    if (path === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(path);
  };

  const handleItemClick = (path) => {
    if (!path) return;

    if (
      location.pathname === "/race" &&
      (path === "/home" || path === "/live-games" || path === "/community")
    ) {
      // If on Race page and navigating to Home, show confirmation modal
      handleOpen(path); // Open the modal
    } else {
      // Navigate directly for other cases
      navigate(path);
      if (isMediumScreen) {
        setOpen(false);
      }
    }
  };

  const handleLogout = () => {
    setOpenDialog(true);
  };

  const mainMenuItems = [
    {
      text: "Home",
      icon: assetIcons.homeSidebar,
      path: routes.home, // Add path for navigation
    },
    {
      text: "Live Games",
      icon: assetIcons.streamSidebar,
      path: routes.live_games, // Add path for navigation
    },
    {
      text: "Community",
      icon: assetIcons.communitySidebar,
      path: routes.community, // Add path for navigation
    },
  ];

  const renderMenuItems = (items, isTopMenu = true) =>
    items.map((item, index) => {
      const isLastBottomItem = !isTopMenu && index === items.length - 1;
      const itemColor = darkColors.sidebarGray;
      const isItemActive = item.path ? isActive(item.path) : false;
      return (
        <React.Fragment key={item.text}>
          <ListItem
            onClick={() =>
              isLastBottomItem ? handleLogout() : handleItemClick(item.path)
            }
            // disablePadding
            sx={{
              mt: 0,
              display: "block",
              // backgroundColor: isItemActive ? "inherit" : "inherit",
              "& .MuiListItemIcon-root, & .MuiListItemText-root": {
                color: isItemActive ? darkColors.primary : itemColor,
              },
              "& svg": {
                fill: isItemActive ? darkColors.primary : itemColor,
              },
            }}
          >
            <ListItemButton
              sx={{
                borderRadius: "12px",
                backgroundImage: isItemActive
                  ? `linear-gradient(to right, ${darkColors.sidebarGreenLeft}, ${darkColors.sidebarGreenRight})`
                  : "none",
                border: isItemActive
                  ? `1px solid ${darkColors.buttonTop}`
                  : "none",
                minHeight: 48,
                justifyContent: open ? "initial" : "center",
                px: 2.5,
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 3 : "auto",
                  justifyContent: "center",
                  color: itemColor, // Apply color here as well
                }}
              >
                <InlineSvg
                  url={item.icon}
                  color={isItemActive ? darkColors.primary : itemColor}
                  sx={{ height: "20px", width: "20px" }}
                />
              </ListItemIcon>
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{
                  style: {
                    fontWeight: 700,
                    opacity: open ? 1 : 0,
                    fontSize: `${fontSizes.heading3}`,
                    // color: itemColor,
                  },
                }}
                sx={
                  {
                    // opacity: open ? 1 : 0,
                    // color: itemColor, // Apply color to text
                  }
                }
              />
            </ListItemButton>
          </ListItem>
          {!isTopMenu && index !== items.length - 1 && (
            <Divider sx={{ color: darkColors.orange }} />
          )}
        </React.Fragment>
      );
    });

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      {/* <LoadingBackdrop isLoading={isLoading} /> */}
      {isMediumScreen ? (
        <MobileBar handleOpen={handleOpen} />
      ) : (
        <Drawer
          variant="permanent"
          open={open}
          sx={{
            "& .MuiDrawer-paper": {
              background: `linear-gradient(to bottom, ${darkColors.backgroundTop} 0%, ${darkColors.backgroundBottom} 100%)`,
            },
          }}
        >
          <Box mt={14} display={"flex"} alignItems={"center"}></Box>
          <Box
            sx={{ display: "flex", flexDirection: "column", height: "100%" }}
          >
            <List>{renderMenuItems(mainMenuItems, true)}</List>
            <Box sx={{ flexGrow: 1 }} />
            {open && (
              <List>
                <Stack
                  direction="row"
                  display={"flex"}
                  justifyContent={"left"}
                  spacing={1.5}
                  sx={{ mb: 2, ml: 2 }}
                >
                  <IconButton
                    size="small"
                    sx={{ color: darkColors.socialIconsGray }}
                    onClick={() =>
                      window.open(socialRedirectUrls.discord, "_blank")
                    }
                  >
                    <img src={assetIcons.discord} alt="discord" />
                  </IconButton>
                  <IconButton
                    size="small"
                    sx={{ color: darkColors.socialIconsGray }}
                    onClick={() =>
                      window.open(socialRedirectUrls.facebook, "_blank")
                    }
                  >
                    <FacebookIcon />
                    {/* <img src={assetIcons.facebook} alt="facebook" /> */}
                  </IconButton>
                  <IconButton
                    size="small"
                    sx={{ color: darkColors.socialIconsGray }}
                    onClick={() =>
                      window.open(socialRedirectUrls.twitter, "_blank")
                    }
                  >
                    <XIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    sx={{ color: darkColors.socialIconsGray }}
                    onClick={() =>
                      window.open(socialRedirectUrls.tiktok, "_blank")
                    }
                  >
                    <img
                      src={assetIcons.tiktok}
                      alt="tiktok"
                      width={30}
                      height={30}
                    />
                  </IconButton>
                </Stack>
                <Divider sx={{ background: darkColors.gray, mx: 2 }} />
                <Box sx={{ width: "230px", ml: 2, mb: 2, mt: 2 }}>
                  <Typography
                    sx={{
                      textAlign: "left",
                      wordWrap: "break-word",
                      whiteSpace: "normal",
                      fontSize: "clamp(12px, 1vw, 14px)", // Responsive font size starting at 14px
                      color: darkColors.gray,
                    }}
                  >
                    {englishTranslations.planetJSidebar}
                  </Typography>
                  <TermsPrivacyLinks />
                </Box>
              </List>
            )}

            {/* Pass true for dividers */}
          </Box>
        </Drawer>
      )}
      <ConfirmationModal
        open={openModal}
        onClose={handleClose}
        img={assetImages.warning} // Dynamic image source
        title="Game Still In Progress"
        message="Are you sure you want to leave?"
        confirmButtonText="BACK TO GAME"
        closeButtonText="GO TO MAIN MENU"
        handleSubmit={handleClose} // Action to be executed on confirm
        secondaryClick={navigateToMainMenu}
      />
    </Box>
  );
}
